import { ApolloError } from "@apollo/client";
import { useEffect, useState } from "react";

import { SNAPSHOT_PENDING_ERROR_CODE } from ".";
import { getGraphQLErrorCode } from "../../utils/errorUtils";

/**
 * Poll cadence used while the report's snapshot is still being created.
 * Long enough not to thrash the resolver (which re-checks snapshot state on
 * every call), short enough that the user doesn't wait noticeably.
 */
export const SNAPSHOT_PENDING_POLL_MS = 10_000;

/**
 * Hard upper bound on how long we'll poll for a pending snapshot. Snapshot
 * jobs finish in seconds-to-minutes; if we're still pending after this we stop
 * polling and show a check-back fallback rather than hammering the resolver
 * forever.
 */
export const SNAPSHOT_PENDING_TIMEOUT_MS = 10 * 60 * 1000;

interface PollableQuery {
  error?: ApolloError;
  startPolling: (pollInterval: number) => void;
  stopPolling: () => void;
}

/**
 * Drives auto-polling while a `tierIIReport` query is throwing SNAPSHOT_PENDING
 * so the report appears as soon as the async snapshot job lands. Polling stops
 * automatically once the query stops returning that code, and is capped by a
 * hard timeout so it can't spin indefinitely.
 *
 * `startPolling` / `stopPolling` from Apollo's `useQuery` are stable across
 * renders, so the timeout is only (re)armed when the pending state actually
 * changes — mirroring the `useLongPollingForJob` pattern.
 */
export function useSnapshotPendingPolling(query: PollableQuery): {
  isPending: boolean;
  pollingTimedOut: boolean;
} {
  const isPending = getGraphQLErrorCode(query.error) === SNAPSHOT_PENDING_ERROR_CODE;
  const [pollingTimedOut, setPollingTimedOut] = useState(false);
  const { startPolling, stopPolling } = query;

  // Reset the timed-out flag whenever we leave the pending state (e.g. the
  // snapshot landed, or the error changed to something else).
  useEffect(() => {
    if (!isPending) setPollingTimedOut(false);
  }, [isPending]);

  useEffect(() => {
    if (!isPending || pollingTimedOut) {
      stopPolling();
      return undefined;
    }

    startPolling(SNAPSHOT_PENDING_POLL_MS);
    const timer = setTimeout(() => setPollingTimedOut(true), SNAPSHOT_PENDING_TIMEOUT_MS);

    return () => {
      clearTimeout(timer);
      stopPolling();
    };
  }, [isPending, pollingTimedOut, startPolling, stopPolling]);

  return { isPending, pollingTimedOut };
}
